import React, { useEffect, useImperativeHandle, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import {
  APIProvider,
  Map,
  useMap,
  useApiLoadingStatus,
  AdvancedMarker as AdvancedMarkerImport,
} from '@vis.gl/react-google-maps';

import { EnvVars } from '../../constants/EnvConfig';
import { logError, log } from '../../utils/logger';

import { AppText } from '../AppText';

/**
 * MapViewWeb architecture notes:
 *
 * This module uses a split MapInner/MapViewWeb pattern because @vis.gl/react-google-maps hooks
 * (useMap, useApiLoadingStatus) must be called inside a component wrapped by <APIProvider>.
 *
 * - MapViewWeb: Renders <APIProvider> and delegates to MapInner
 * - MapInner: Consumes the Google Maps context and renders the actual <Map> component
 *
 * The onIdle callback pattern is used instead of onRegionChangeComplete because the vis.gl
 * library fires idle when the map becomes stable after panning/zooming, which aligns with
 * React Native Maps' onRegionChangeComplete semantics.
 *
 * The @vis.gl/react-google-maps dependencies are injected through the `googleMaps` prop rather
 * than closed over from the module import directly. This mirrors the `mapViewImpl` seam
 * MapViewMobile uses for react-native-maps, and lets tests supply an in-memory mock bundle
 * instead of relying on a static Jest module mock. MapView.web.js wires the real library in.
 * The default below keeps production behaviour unchanged and, critically, guarantees that
 * APIProvider/Map/hooks and the re-exported AdvancedMarker all come from the same module
 * instance (a mismatch breaks the Google Maps context — see the AdvancedMarker note below).
 */

// The real library, statically imported. Used both as the default `googleMaps` bundle and as the
// source of the AdvancedMarker re-export, so both resolve to the same module instance.
const defaultGoogleMaps = {
  APIProvider,
  Map,
  useMap,
  useApiLoadingStatus,
};

const errorStyles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e8e8e8',
  },
  message: {
    textAlign: 'center',
  },
});

const MapInner = ({ initialRegion, onRegionChangeComplete, onPress, children, mapViewRef, googleMaps }) => {
  const { Map: MapComponent, useMap: useMapHook, useApiLoadingStatus: useApiLoadingStatusHook } = googleMaps;

  const map = useMapHook();
  const status = useApiLoadingStatusHook();

  useEffect(() => {
    if (!EnvVars.googleMapsApiKey) {
      logError(new Error('MapViewWeb: googleMapsApiKey is not configured'));
    }
    if (!EnvVars.googleMapsMapId) {
      logError(new Error('MapViewWeb: googleMapsMapId is not configured'));
    }
  }, []);

  useEffect(() => {
    if (status === 'FAILED') {
      logError(new Error('MapViewWeb: Google Maps API failed to load'));
    }
  }, [status]);

  // Constrain the interface exposed to the parent to only the platform-agnostic API.
  // The underlying @vis.gl/react-google-maps map instance has a much larger surface,
  // but callers should only use animateToRegion for consistency with MapViewMobile.
  //
  // https://react.dev/reference/react/useImperativeHandle
  useImperativeHandle(
    mapViewRef,
    () => ({
      animateToRegion(mapViewRegion) {
        if (map) {
          map.panTo({ lat: mapViewRegion.latitude, lng: mapViewRegion.longitude });
          if (mapViewRegion.zoom != null) {
            map.setZoom(mapViewRegion.zoom);
          }
        } else {
          log('MapViewWeb: animateToRegion called before map ready');
        }
      },
    }),
    [map]
  );

  const handleIdle = useCallback(() => {
    if (!onRegionChangeComplete || !map) return;

    // All Google Maps API calls (getCenter, getZoom, getBounds) are wrapped together because
    // they must all succeed to construct a valid MapViewWebRegion. If any call fails (e.g. the
    // map is in an intermediate state), we log and skip this idle event. Errors from the
    // onRegionChangeComplete callback intentionally propagate to the caller.
    let center;
    let zoom;
    let bounds;
    try {
      center = map.getCenter();
      zoom = map.getZoom();
      bounds = map.getBounds();
    } catch (err) {
      logError(err);
      return;
    }

    if (!center) return;
    const r = new MapViewWebRegion(center.lat(), null, center.lng(), null, zoom);
    r.mapImpl = { getBounds: () => bounds };

    onRegionChangeComplete(r);
  }, [onRegionChangeComplete, map]);

  if (status === 'FAILED') {
    return (
      <View style={errorStyles.container}>
        <AppText style={errorStyles.message}>Unable to load map. Please try again later.</AppText>
      </View>
    );
  }

  return (
    <MapComponent
      defaultCenter={{ lat: initialRegion.latitude, lng: initialRegion.longitude }}
      defaultZoom={initialRegion.zoom ?? 12}
      mapId={EnvVars.googleMapsMapId}
      onIdle={handleIdle}
      onClick={onPress}
      style={{ width: '100%', height: '100%' }}
      disableDefaultUI={true}
      zoomControl={true}
      gestureHandling="greedy"
    >
      {children}
    </MapComponent>
  );
};

export const MapViewWeb = (props) => {
  const { initialRegion, onRegionChangeComplete, onPress, children, mapViewRef, style, googleMaps } = props;

  // Fall back to the real, statically-imported library when no bundle is injected (production).
  const maps = googleMaps ?? defaultGoogleMaps;
  const { APIProvider: APIProviderComponent } = maps;

  return (
    <View style={[{ flex: 1 }, style]}>
      <APIProviderComponent apiKey={EnvVars.googleMapsApiKey} libraries={['marker']}>
        <MapInner
          initialRegion={initialRegion}
          onRegionChangeComplete={onRegionChangeComplete}
          onPress={onPress}
          mapViewRef={mapViewRef}
          googleMaps={maps}
        >
          {children}
        </MapInner>
      </APIProviderComponent>
    </View>
  );
};

// TODO: When converting to TypeScript, define an interface for mapImpl (getBounds() returning
// { getNorthEast(), getSouthWest() } with lat()/lng() methods) for type safety.
export class MapViewWebRegion {
  constructor(latitude, latitudeDelta, longitude, longitudeDelta, __zoom) {
    this.latitude = latitude;
    this.latitudeDelta = latitudeDelta;
    this.longitude = longitude;
    this.longitudeDelta = longitudeDelta;

    this.mapImpl = null;
    this.zoom = __zoom || this.#regionDeltaToZoomLevel(longitudeDelta);

    this.identity = [this.latitude, this.longitude, this.zoom].join('|');
  }

  #regionDeltaToZoomLevel(delta) {
    return Math.round(Math.log(360 / delta) / Math.LN2) + 1;
  }

  // Must invert #regionDeltaToZoomLevel exactly (delta -> zoom is `round(log2(360/delta)) + 1`,
  // so zoom -> delta is `360 / 2^(zoom - 1)`) -- this previously read
  // `360 / Math.exp(zoomLevel * Math.LN2) + 1`, which is neither the correct exponent (missing
  // the "- 1") nor added in the right place (+1 to the whole delta, not inside the exponent).
  // That produced a delta ~4x too large for a typical zoom level, which only ever showed up when
  // this fallback (no real map bounds yet) fed into a search radius calculation, silently
  // widening it (e.g. clamping straight to the endpoint's 80km max) rather than throwing.
  #zoomLevelToRegionDelta(zoomLevel) {
    return 360 / Math.exp((zoomLevel - 1) * Math.LN2);
  }

  asNativeRegion() {
    const centre = {
      latitude: this.latitude,
      longitude: this.longitude,
    };

    if (this.mapImpl) {
      const mapBounds = this.mapImpl.getBounds();
      const boundingNorthEast = mapBounds.getNorthEast();
      const boundingSouthWest = mapBounds.getSouthWest();

      const north = boundingNorthEast.lat();
      const south = boundingSouthWest.lat();
      const east = boundingNorthEast.lng();
      const west = boundingSouthWest.lng();

      const deltaEnrichedRegion = {
        ...centre,

        latitudeDelta: Math.abs(north - south) / 2,
        longitudeDelta: Math.abs(west - east) / 2,
      };

      return deltaEnrichedRegion;
    }

    // Because a zoom level is coarser than deltas, converting from a zoom level to deltas
    // always returns the lowest deltas needed to achieve a zoom level, not the deltas that
    // were originally used to determine the zoom level.
    const delta = this.#zoomLevelToRegionDelta(this.zoom);
    return {
      ...centre,
      latitudeDelta: delta,
      longitudeDelta: delta,
    };
  }

  center(latitude, longitude) {
    return new MapViewWebRegion(latitude, this.latitudeDelta, longitude, this.longitudeDelta, this.zoom);
  }
}

// Re-export AdvancedMarker from the same ESM entry point used by APIProvider/Map.
// Importing directly in other files can create a separate React context that
// doesn't match the APIProviderContext, causing markers not to render.
export const AdvancedMarker = AdvancedMarkerImport;
